import { router, useFocusEffect } from 'expo-router';
import { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';

import {
  useGetNonProfitLightRatingQuestions,
  useGetNPOLightRatingQuestionsAnswers,
  useUpdateNonProfitOrgLightRatingQuestions
} from '@/api/hooks/nonProfitOrgHooks';
import DotsLoader from '@/components/DotsLoader';
import NpoLightRatingQuestionsForm from '@/components/forms/NpoLightRatingQuestionsForm';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { LightRatingQuestionsData } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';

const NpoLightRatingQuestions = () => {
  const { state: authState } = useAuth();
  const { data: questionOptions } = useGetNonProfitLightRatingQuestions();
  const {
    data: npoQuestionAnswers,
    isFetching,
    refetch
  } = useGetNPOLightRatingQuestionsAnswers({
    id: authState.user?.user.nonProfitOrgId ?? ''
  });
  const { mutateAsync: updateLightRatingQuestions, isPending: lightRatingQuestionsPending } =
    useUpdateNonProfitOrgLightRatingQuestions();

  const { showSuccess } = useEndFlowModal();

  useFocusEffect(
    useCallback(() => {
      if (authState.user?.user?.nonProfitOrgId) refetch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );

  const initialData =
    npoQuestionAnswers === undefined || npoQuestionAnswers.length === 0
      ? questionOptions?.map((option) => ({ id: option.id, answer: null }))
      : npoQuestionAnswers?.map((questionAnswer) => ({
          id: questionAnswer.questionId,
          answer: questionAnswer.answer === true ? 'Yes' : questionAnswer.answer === false ? 'No' : null
        }));

  const onSubmit = async (data: LightRatingQuestionsData) => {
    try {
      const mappedAnswers = data.questions.map((question) => ({
        questionId: question.id,
        answer: question.answer === 'Yes' ? 'true' : question.answer === 'No' ? 'false' : null
      }));

      updateLightRatingQuestions(
        {
          npoId: authState.signUpData?.loginEmail?.npoId ?? '',
          answers: mappedAnswers
        },
        {
          onSuccess: () => {
            showSuccess({
              title: i18n.t('end_flow.success_title'),
              message: i18n.t('npo_details.update_message')
            });
            router.back();
          }
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  if (isFetching || !npoQuestionAnswers) {
    return (
      <View style={styles.container}>
        <DotsLoader />
      </View>
    );
  }

  return (
    <NpoLightRatingQuestionsForm
      initialData={initialData}
      questionOptions={questionOptions ?? []}
      isLoading={lightRatingQuestionsPending}
      onSubmitTapped={onSubmit}
      showBackButton={false}
      showSaveAndContinueButton={false}
      submitLabel={i18n.t('mix.done')}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  }
});

export default NpoLightRatingQuestions;
